import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#ffffff',
      fontFamily: '"Inter", sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': { color: '#71717a' },
      iconColor: '#d4af37',
    },
    invalid: { color: '#f87171', iconColor: '#f87171' },
  },
};

// ── Card capture form (Founder / Pre-Launch) ─────────────────────────────────
function CardForm({ plan, onSuccess }: { plan: string; onSuccess: (email: string) => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [email, setEmail] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const planLabel = plan === 'founder' ? 'Founder — $25/mo · $300 billed annually' : 'Pre-Launch — $79/mo';
  const planColor = plan === 'founder' ? '#d4af37' : '#8B4513';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!email || !email.includes('@')) { setError('Enter a valid email address.'); return; }
    if (!agreed) { setError('Please agree to the Terms of Service to continue.'); return; }

    setIsSubmitting(true);
    setError('');

    try {
      const res = await fetch('/api/create-setup-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, plan }),
      });

      const data = await res.json() as { clientSecret?: string; error?: string };
      if (!res.ok || !data.clientSecret) throw new Error(data.error || 'Failed to initialize payment');

      const { error: stripeError } = await stripe.confirmCardSetup(data.clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement)!,
          billing_details: { email },
        },
      });

      if (stripeError) throw new Error(stripeError.message);
      onSuccess(email);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3 max-w-[560px] mx-auto">
      <div className="text-center mb-1">
        <span className="text-sm font-semibold px-3 py-1 rounded-full" style={{ color: planColor, background: `${planColor}15`, border: `1px solid ${planColor}30` }}>
          {planLabel} · No charge until launch
        </span>
      </div>

      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email address"
        required
        disabled={isSubmitting}
        className="w-full bg-[#1a1a1d] text-white text-[16px] px-5 py-4 rounded-xl border-2 border-transparent focus:border-[#d4af37] focus:outline-none transition-colors disabled:opacity-50"
      />

      <div className="bg-[#1a1a1d] px-5 py-4 rounded-xl border-2 border-transparent focus-within:border-[#d4af37] transition-colors">
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>

      <label className="flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-1 w-4 h-4 rounded border-[#3a3a3d] bg-[#1a1a1d] accent-[#d4af37] cursor-pointer flex-shrink-0"
        />
        <span className="text-[#a1a1aa] text-xs leading-relaxed group-hover:text-[#d4d4d8] transition-colors">
          I agree to the{' '}
          <Link to="/terms" target="_blank" className="text-[#d4af37] hover:underline">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" target="_blank" className="text-[#d4af37] hover:underline">Privacy Policy</Link>
          . I understand my card will not be charged until launch.
        </span>
      </label>

      {error && (
        <p className="text-red-400 text-sm text-center">{error}</p>
      )}

      <button
        type="submit"
        disabled={!stripe || isSubmitting || !agreed}
        className="w-full bg-[#d4af37] text-black text-[16px] font-bold px-8 py-4 rounded-xl hover:shadow-[0_0_30px_rgba(212,175,55,0.6)] transition-all duration-200 animate-pulse-glow disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
      >
        {isSubmitting ? 'Reserving your spot...' : 'Reserve My Spot — No Charge Until Launch'}
      </button>

      <p className="text-[#71717a] text-xs text-center flex items-center justify-center gap-1">
        <i className="ri-lock-fill text-[#d4af37] text-sm"></i>
        Card secured by Stripe. You won't be charged until we launch.
      </p>
    </form>
  );
}

// ── Email-only form (Waitlist) ────────────────────────────────────────────────
function WaitlistForm({ onSuccess }: { onSuccess: (email: string) => void }) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) { setError('Enter a valid email address.'); return; }

    setIsSubmitting(true);
    setError('');
    try {
      const { supabase } = await import('../../../lib/supabase');
      const { error: dbError } = await supabase
        .from('waitlist')
        .upsert({ email, plan: 'waitlist', source: 'landing_page' }, { onConflict: 'email' });
      if (dbError) throw dbError;
      onSuccess(email);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3 max-w-[560px] mx-auto">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email address"
        required
        disabled={isSubmitting}
        className="w-full bg-[#1a1a1d] text-white text-[16px] px-5 py-4 rounded-xl border-2 border-transparent focus:border-[#06b6d4] focus:outline-none transition-colors disabled:opacity-50"
      />
      {error && <p className="text-red-400 text-sm text-center">{error}</p>}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-[#52525b] text-white text-[16px] font-bold px-8 py-4 rounded-xl hover:bg-[#71717a] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
      >
        {isSubmitting ? 'Joining...' : 'Join Waitlist — Standard Pricing at Launch'}
      </button>
      <p className="text-[#71717a] text-xs text-center flex items-center justify-center gap-1">
        <i className="ri-lock-fill text-[#06b6d4] text-sm"></i>
        No spam. Just your early access notification.
      </p>
    </form>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function FinalCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const [plan, setPlan] = useState<'founder' | 'prelaunch' | 'waitlist'>('founder');
  const [success, setSuccess] = useState<{ email: string; plan: string } | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold: 0.3 });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSuccess = useCallback((email: string) => {
    setSuccess({ email, plan });
  }, [plan]);

  const isCardPlan = plan === 'founder' || plan === 'prelaunch';

  const planTabs = [
    { id: 'founder' as const, label: 'Founder', sub: '$25/mo · $300/yr · locked forever', color: '#d4af37' },
    { id: 'prelaunch' as const, label: 'Pre-Launch', sub: '$79/mo · cancel anytime', color: '#8B4513' },
    { id: 'waitlist' as const, label: 'Waitlist', sub: 'Standard pricing at launch', color: '#52525b' },
  ];

  return (
    <section id="final-cta" ref={sectionRef} className="relative py-48 overflow-hidden">
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover"
          style={{
            backgroundImage: 'url(/images/cta-bg.jpg)',
            backgroundPosition: 'center 35%',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
      </div>

      <div className="relative z-10 max-w-[700px] mx-auto px-5 sm:px-8 text-center">
        <h2 className={`text-white text-[28px] sm:text-[44px] lg:text-[56px] font-bold leading-tight mb-4 sm:mb-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          For Attorneys Who Refuse to Compromise
        </h2>

        <p className={`text-[#d4d4d8] text-[16px] sm:text-[18px] mb-8 sm:mb-10 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          Founder pricing closes at launch. No exceptions.
        </p>

        {success ? (
          <div className={`bg-[#0f0f11]/80 border border-[#d4af37]/40 rounded-2xl p-8 max-w-[500px] mx-auto transition-all duration-500`}>
            {isCardPlan ? (
              <>
                <div className="text-4xl mb-4">🏆</div>
                <h3 className="text-white text-xl font-bold mb-2">Your spot is reserved.</h3>
                <p className="text-[#d4d4d8] text-sm mb-3">
                  Card on file for <span className="text-[#d4af37] font-semibold">{success.email}</span>
                </p>
                <p className="text-[#a1a1aa] text-sm">
                  {success.plan === 'founder'
                    ? "We'll charge $300 (one year) when we launch — not before. Rate locked forever at $25/mo."
                    : "We'll charge your card when we launch — not before."}
                  {' '}You'll get early access before anyone else.
                </p>
              </>
            ) : (
              <>
                <div className="text-4xl mb-4">✉️</div>
                <h3 className="text-white text-xl font-bold mb-2">You're on the list.</h3>
                <p className="text-[#d4d4d8] text-sm">
                  We'll let <span className="text-[#06b6d4] font-semibold">{success.email}</span> know when we launch.
                </p>
              </>
            )}
          </div>
        ) : (
          <div className={`transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* Plan tabs */}
            <div className="flex justify-center gap-2 mb-6 flex-wrap">
              {planTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setPlan(tab.id)}
                  className={`flex flex-col items-center px-5 py-3 rounded-xl border-2 transition-all cursor-pointer ${
                    plan === tab.id
                      ? 'border-[#d4af37] bg-[#1a1a1d]'
                      : 'border-[#2a2a2d] bg-[#1a1a1d]/50 hover:border-[#3a3a3d]'
                  }`}
                >
                  <span className="text-white text-sm font-semibold">{tab.label}</span>
                  <span className="text-[#71717a] text-xs">{tab.sub}</span>
                </button>
              ))}
            </div>

            {/* Form */}
            {isCardPlan ? (
              <Elements stripe={stripePromise}>
                <CardForm plan={plan} onSuccess={handleSuccess} />
              </Elements>
            ) : (
              <WaitlistForm onSuccess={handleSuccess} />
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 15px rgba(212, 175, 55, 0.3); }
          50% { box-shadow: 0 0 30px rgba(212, 175, 55, 0.5); }
        }
        .animate-pulse-glow { animation: pulse-glow 2s ease-in-out infinite; }
      `}</style>
    </section>
  );
}
